import React, { useMemo, useRef, useState, useCallback, useLayoutEffect, useEffect } from 'react';
import { ZoomIn, ZoomOut, Maximize2, Minimize2, Scan, Check } from 'lucide-react';
import { COLOR_CRITICO } from '../gantt/TaskBar';

// ---------- Red PERT/CPM ----------
//
// Diagrama de nodos por niveles: cada columna es la profundidad de la tarea en
// la red (cuántas dependencias hay antes de ella). Dentro de cada columna el
// orden se ajusta con dos barridos de baricentro para reducir cruces. Los
// números (ES/EF/LS/LF/holgura) vienen calculados del backend.
//
// Zoom con los botones o Ctrl + rueda; paneo arrastrando el fondo o con el
// scroll normal. Click en un nodo abre la tarea. "Pantalla completa" cubre la
// ventana con la red (Esc para salir) y la reajusta al nuevo tamaño.

const NODE_W = 216;
const NODE_H = 104;
const GAP_X = 72;
const GAP_Y = 22;
const PAD = 32;
const ESCALAS = [0.2, 0.3, 0.4, 0.55, 0.7, 0.85, 1, 1.2];

const DIA_MS = 86400000;

const fechaDesdeOffset = (ancla, offset) => {
  const d = new Date(ancla + 'T00:00:00');
  return new Date(d.getTime() + offset * DIA_MS);
};

const fmt = (d) => d.toLocaleDateString('es-VE', { day: 'numeric', month: 'short' }).replace('.', '');

const recortar = (texto, max) => (texto && texto.length > max ? `${texto.slice(0, max - 1).trimEnd()}…` : texto || '');

// Columnas por profundidad + orden por baricentro.
const calcularLayout = (nodos) => {
  const porId = new Map(nodos.map((n) => [n.id, n]));
  const nivel = new Map();
  // El backend devuelve los nodos en orden topológico.
  nodos.forEach((n) => {
    const niveles = n.predecesoras.filter((p) => nivel.has(p)).map((p) => nivel.get(p) + 1);
    nivel.set(n.id, niveles.length ? Math.max(...niveles) : 0);
  });

  const columnas = [];
  nodos.forEach((n) => {
    const c = nivel.get(n.id);
    if (!columnas[c]) columnas[c] = [];
    columnas[c].push(n.id);
  });
  columnas.forEach((col) => col.sort((a, b) => (porId.get(a).es - porId.get(b).es) || (porId.get(a).numero - porId.get(b).numero)));

  const indice = () => {
    const idx = new Map();
    columnas.forEach((col) => col.forEach((id, i) => idx.set(id, i)));
    return idx;
  };
  const baricentro = (ids, idx) => {
    const vs = ids.filter((v) => idx.has(v)).map((v) => idx.get(v));
    return vs.length ? vs.reduce((a, b) => a + b, 0) / vs.length : null;
  };
  // Reordena una columna por el baricentro de sus vecinos (predecesoras o
  // sucesoras) en las columnas ya ubicadas.
  const reordenar = (c, campo) => {
    const idx = indice();
    const col = columnas[c];
    const clave = new Map(col.map((id, i) => [id, baricentro(porId.get(id)[campo], idx) ?? i]));
    col.sort((a, b) => clave.get(a) - clave.get(b));
  };
  for (let pasada = 0; pasada < 2; pasada += 1) {
    for (let c = 1; c < columnas.length; c += 1) reordenar(c, 'predecesoras');
    for (let c = columnas.length - 2; c >= 0; c -= 1) reordenar(c, 'sucesoras');
  }

  const maxFilas = Math.max(0, ...columnas.map((c) => c.length));
  const altoTotal = maxFilas * NODE_H + Math.max(0, maxFilas - 1) * GAP_Y;
  const pos = new Map();
  columnas.forEach((col, c) => {
    const alto = col.length * NODE_H + Math.max(0, col.length - 1) * GAP_Y;
    const y0 = PAD + (altoTotal - alto) / 2;
    col.forEach((id, i) => {
      pos.set(id, { x: PAD + c * (NODE_W + GAP_X), y: y0 + i * (NODE_H + GAP_Y) });
    });
  });
  return {
    pos,
    width: PAD * 2 + columnas.length * NODE_W + Math.max(0, columnas.length - 1) * GAP_X,
    height: PAD * 2 + altoTotal,
  };
};

const PertDiagram = ({ data, onAbrirTarea, slideOverAbierto = false }) => {
  const scrollRef = useRef(null);
  const [escala, setEscala] = useState(1);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const [hover, setHover] = useState(null);
  const arrastre = useRef(null);

  const { conectados, aislados } = useMemo(() => {
    const nodos = data?.nodos || [];
    return {
      conectados: nodos.filter((n) => n.predecesoras.length || n.sucesoras.length),
      aislados: nodos.filter((n) => !n.predecesoras.length && !n.sucesoras.length),
    };
  }, [data]);

  const layout = useMemo(() => calcularLayout(conectados), [conectados]);
  const porId = useMemo(() => new Map((data?.nodos || []).map((n) => [n.id, n])), [data]);

  // Vecinos del nodo en hover: se atenúa el resto para seguir una cadena.
  const resaltados = useMemo(() => {
    if (!hover) return null;
    const n = porId.get(hover);
    if (!n) return null;
    return new Set([hover, ...n.predecesoras, ...n.sucesoras]);
  }, [hover, porId]);

  const ajustar = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !layout.width) return;
    const s = Math.min(1, (el.clientWidth - 16) / layout.width, (el.clientHeight - 16) / layout.height);
    setEscala(Math.max(ESCALAS[0], Math.round(s * 100) / 100));
  }, [layout]);

  // Al entrar o salir de pantalla completa cambia el tamaño visible: reajustar
  // cuando el navegador ya aplicó el nuevo layout.
  const primeraVez = useRef(true);
  useEffect(() => {
    if (primeraVez.current) { primeraVez.current = false; return undefined; }
    const id = requestAnimationFrame(() => ajustar());
    return () => cancelAnimationFrame(id);
  }, [pantallaCompleta]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc sale de pantalla completa (si hay una tarea abierta, Esc la cierra a ella).
  useEffect(() => {
    if (!pantallaCompleta) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !slideOverAbierto) setPantallaCompleta(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pantallaCompleta, slideOverAbierto]);

  // Primer render: ajustar si la red no cabe.
  const yaAjusto = useRef(false);
  useLayoutEffect(() => {
    if (yaAjusto.current || !layout.width) return;
    yaAjusto.current = true;
    ajustar();
  }, [layout, ajustar]);

  const zoom = (dir) => {
    setEscala((s) => {
      if (dir > 0) return ESCALAS.find((e) => e > s + 0.001) || s;
      return [...ESCALAS].reverse().find((e) => e < s - 0.001) || s;
    });
  };

  // Ctrl/Cmd + rueda = zoom. Listener nativo no pasivo: React registra
  // `wheel` como pasivo y ahí preventDefault no evita el zoom del navegador.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      setEscala((s) => {
        if (dir > 0) return ESCALAS.find((x) => x > s + 0.001) || s;
        return [...ESCALAS].reverse().find((x) => x < s - 0.001) || s;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onMouseDown = (e) => {
    if (e.button !== 0 || e.target.closest('[data-nodo]')) return;
    const el = scrollRef.current;
    arrastre.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
    el.style.cursor = 'grabbing';
  };
  const onMouseMove = (e) => {
    const a = arrastre.current;
    if (!a) return;
    const el = scrollRef.current;
    el.scrollLeft = a.sl - (e.clientX - a.x);
    el.scrollTop = a.st - (e.clientY - a.y);
  };
  const soltar = () => {
    arrastre.current = null;
    if (scrollRef.current) scrollRef.current.style.cursor = '';
  };

  const aristas = useMemo(() => (data?.aristas || [])
    .filter((a) => layout.pos.has(a.de) && layout.pos.has(a.a))
    .map((a) => {
      const de = porId.get(a.de);
      const hacia = porId.get(a.a);
      return { ...a, critica: de.critica && hacia.critica && hacia.es === de.ef };
    })
    .sort((a, b) => Number(a.critica) - Number(b.critica)), [data, layout, porId]);

  const ancla = data?.ancla;

  return (
    <div className={pantallaCompleta ? 'fixed inset-0 z-[60] flex flex-col bg-white' : 'relative flex-1 min-h-0 flex flex-col'}>
      {/* Controles */}
      <div className="absolute right-4 top-3 z-10 flex items-center gap-1 rounded-md bg-white/90 backdrop-blur ring-1 ring-slate-200 shadow-sm p-0.5">
        <button onClick={() => zoom(-1)} title="Alejar" className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded">
          <ZoomOut size={14} />
        </button>
        <span className="text-[11px] text-slate-500 tabular-nums w-10 text-center">{Math.round(escala * 100)}%</span>
        <button onClick={() => zoom(1)} title="Acercar" className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded">
          <ZoomIn size={14} />
        </button>
        <div className="w-px h-4 bg-slate-200 mx-0.5" />
        <button onClick={ajustar} title="Ajustar la red al espacio visible" className="inline-flex items-center gap-1 px-1.5 py-1 text-[11px] text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded">
          <Scan size={13} />
          Ajustar
        </button>
        <button
          onClick={() => setPantallaCompleta((v) => !v)}
          title={pantallaCompleta ? 'Salir de pantalla completa (Esc)' : 'Pantalla completa'}
          aria-pressed={pantallaCompleta}
          className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
        >
          {pantallaCompleta ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
        </button>
      </div>

      {/* Leyenda */}
      <div className="absolute left-4 top-3 z-10 flex items-center gap-3 rounded-md bg-white/90 backdrop-blur ring-1 ring-slate-200 shadow-sm px-2.5 py-1 text-[11px] text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm" style={{ border: `2px solid ${COLOR_CRITICO}` }} />
          Ruta crítica
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm border border-dashed border-slate-400" />
          Esperando predecesora
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm bg-slate-100 border border-slate-200" />
          Hecha
        </span>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-auto bg-[radial-gradient(circle,rgba(148,163,184,0.25)_1px,transparent_1px)] [background-size:20px_20px] select-none"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={soltar}
        onMouseLeave={soltar}
      >
        {conectados.length > 0 ? (
          <svg
            width={layout.width * escala}
            height={layout.height * escala}
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            className="block"
            style={{ minWidth: layout.width * escala }}
          >
            <defs>
              <marker id="pert-flecha" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M 0 0 L 8 4 L 0 8 z" fill="#94a3b8" />
              </marker>
              <marker id="pert-flecha-critica" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M 0 0 L 8 4 L 0 8 z" fill={COLOR_CRITICO} />
              </marker>
            </defs>

            {aristas.map((a) => {
              const p1 = layout.pos.get(a.de);
              const p2 = layout.pos.get(a.a);
              const x1 = p1.x + NODE_W;
              const y1 = p1.y + NODE_H / 2;
              const x2 = p2.x - 2;
              const y2 = p2.y + NODE_H / 2;
              const dx = Math.max(36, (x2 - x1) / 2);
              const atenuada = resaltados && !(resaltados.has(a.de) && resaltados.has(a.a) && (a.de === hover || a.a === hover));
              return (
                <path
                  key={`${a.de}-${a.a}`}
                  d={`M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`}
                  fill="none"
                  stroke={a.critica ? COLOR_CRITICO : '#94a3b8'}
                  strokeWidth={a.critica ? 2.25 : 1.25}
                  markerEnd={a.critica ? 'url(#pert-flecha-critica)' : 'url(#pert-flecha)'}
                  opacity={atenuada ? 0.18 : 1}
                />
              );
            })}

            {conectados.map((n) => {
              const p = layout.pos.get(n.id);
              return (
                <NodoPert
                  key={n.id}
                  nodo={n}
                  x={p.x}
                  y={p.y}
                  ancla={ancla}
                  atenuado={resaltados && !resaltados.has(n.id)}
                  onClick={() => onAbrirTarea(n)}
                  onHover={setHover}
                />
              );
            })}
          </svg>
        ) : (
          <div className="flex items-center justify-center h-full min-h-[240px] px-6">
            <div className="max-w-md text-center">
              <p className="text-sm font-medium text-slate-800">Todavía no hay una red que dibujar</p>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
                La red se arma con las dependencias entre tareas. Abre una tarea y agrega en
                "Bloqueada por" la tarea que tiene que terminar antes; la ruta crítica aparece sola.
              </p>
            </div>
          </div>
        )}

        {aislados.length > 0 && (
          <div className="px-6 pb-8 pt-2">
            <p className="text-xs font-medium text-slate-500 mb-2">
              Sin dependencias <span className="text-slate-400 tabular-nums">{aislados.length}</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {aislados.map((n) => (
                <button
                  key={n.id}
                  data-nodo
                  onClick={() => onAbrirTarea(n)}
                  className={`inline-flex items-center gap-2 max-w-[280px] px-2.5 py-1.5 rounded-md bg-white text-left text-xs ring-1 hover:ring-slate-300 transition-shadow ${
                    n.hecha ? 'ring-slate-200 opacity-60' : 'ring-slate-200'
                  }`}
                  style={n.critica ? { boxShadow: `0 0 0 1.5px ${COLOR_CRITICO}` } : undefined}
                  title={n.critica ? 'En la ruta crítica' : `Holgura ${n.holgura} d`}
                >
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: n.estado_color || '#94a3b8' }} />
                  <span className="text-slate-400 tabular-nums flex-shrink-0">{n.codigo}</span>
                  <span className="text-slate-700 truncate">{n.titulo}</span>
                  {n.retraso_dias > 0 && !n.hecha && (
                    <span className="text-rose-600 tabular-nums flex-shrink-0">+{n.retraso_dias} d</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Nodo ----------

const NodoPert = ({ nodo: n, x, y, ancla, atenuado, onClick, onHover }) => {
  const inicioT = fechaDesdeOffset(ancla, n.es);
  const finT = fechaDesdeOffset(ancla, n.ef - 1);
  const inicioL = fechaDesdeOffset(ancla, n.ls);
  const finL = fechaDesdeOffset(ancla, n.lf - 1);
  const esperando = !n.hecha && n.esperando_a > 0;
  const borde = n.critica ? COLOR_CRITICO : '#cbd5e1';
  const colorEstado = n.estado_color || '#94a3b8';
  const asignado = n.asignados?.[0]?.nombre;

  const tooltip = [
    `${n.codigo} · ${n.titulo}`,
    `Duración: ${n.duracion} d`,
    `Inicio temprano ${fmt(inicioT)} (día ${n.es}), fin temprano ${fmt(finT)} (día ${n.ef})`,
    `Inicio tardío ${fmt(inicioL)} (día ${n.ls}), fin tardío ${fmt(finL)} (día ${n.lf})`,
    n.critica ? 'Holgura 0: en la ruta crítica' : `Holgura: ${n.holgura} d`,
    esperando ? `Esperando ${n.esperando_a} predecesora(s)` : null,
    n.retraso_dias > 0 ? `${n.retraso_dias} d de retraso` : null,
    n.sin_fechas ? 'Sin fechas: se cuenta como 1 día' : null,
    asignado ? `Asignada a ${n.asignados.map((a) => a.nombre).join(', ')}` : null,
  ].filter(Boolean).join('\n');

  return (
    <g
      data-nodo
      transform={`translate(${x}, ${y})`}
      onClick={onClick}
      onMouseEnter={() => onHover(n.id)}
      onMouseLeave={() => onHover(null)}
      className="cursor-pointer"
      opacity={atenuado ? 0.3 : 1}
      style={{ transition: 'opacity 120ms' }}
    >
      <title>{tooltip}</title>
      <rect
        width={NODE_W}
        height={NODE_H}
        rx={8}
        fill={n.hecha ? '#f8fafc' : '#ffffff'}
        stroke={borde}
        strokeWidth={n.critica ? 2 : 1}
        strokeDasharray={esperando ? '5 3' : undefined}
      />
      {/* Cabecera: estado + código + duración */}
      <circle cx={14} cy={16} r={3.5} fill={colorEstado} />
      <text x={24} y={20} fontSize={11} fontWeight={600} fill={n.critica ? COLOR_CRITICO : '#64748b'} className="tabular-nums">
        {n.codigo}
      </text>
      {n.hecha && (
        <g transform={`translate(${NODE_W - 64}, 8)`}>
          <Check size={13} color="#10b981" strokeWidth={2.5} />
        </g>
      )}
      <text x={NODE_W - 12} y={20} fontSize={11} fill="#64748b" textAnchor="end" className="tabular-nums">
        {n.duracion} d
      </text>
      {/* Título */}
      <text x={12} y={39} fontSize={12.5} fontWeight={500} fill={n.hecha ? '#64748b' : '#0f172a'}>
        {recortar(n.titulo, 28)}
      </text>
      <text x={12} y={54} fontSize={10.5} fill="#94a3b8">
        {recortar(asignado || 'Sin asignar', 18)}
      </text>
      {n.retraso_dias > 0 && !n.hecha ? (
        <text x={NODE_W - 12} y={54} fontSize={10.5} fontWeight={600} fill="#e11d48" textAnchor="end" className="tabular-nums">
          +{n.retraso_dias} d de atraso
        </text>
      ) : (
        <text x={NODE_W - 12} y={54} fontSize={10.5} fontWeight={n.critica ? 600 : 400} fill={n.critica ? COLOR_CRITICO : '#64748b'} textAnchor="end" className="tabular-nums">
          holgura {n.holgura} d
        </text>
      )}
      {/* Fechas tempranas y tardías (ES-EF / LS-LF) */}
      <line x1={0} x2={NODE_W} y1={62} y2={62} stroke="#e2e8f0" />
      <text x={12} y={79} fontSize={10.5} fill="#94a3b8">Temprano</text>
      <text x={NODE_W - 12} y={79} fontSize={10.5} fill="#334155" textAnchor="end" className="tabular-nums">{fmt(inicioT)} – {fmt(finT)}</text>
      <text x={12} y={95} fontSize={10.5} fill="#94a3b8">Tardío</text>
      <text x={NODE_W - 12} y={95} fontSize={10.5} fill="#334155" textAnchor="end" className="tabular-nums">{fmt(inicioL)} – {fmt(finL)}</text>
    </g>
  );
};

export default PertDiagram;
